import React, { useState, useEffect } from 'react';
import { Layout, Card, Row, Col, Typography, Spin, Alert, Space, Divider } from 'antd';
import { CalendarOutlined } from '@ant-design/icons';
import axios from 'axios';
import withRoleAccess from '../../hoc/withRoleAccess';
import Sidebar from '../../components/Sidebar/Sidebar';
import Header from '../../components/Header/Header';
import { useTranslation } from 'react-i18next';
import './AnnouncementList.css';

const { Content } = Layout;
const { Title, Paragraph, Text } = Typography;

const AnnouncementPage = () => {
  const { i18n } = useTranslation();
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const API_URL = process.env.REACT_APP_API_URL;

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const options = { day: '2-digit', month: 'short', year: '2-digit' };
    return new Intl.DateTimeFormat(i18n.language, options).format(date).toUpperCase();
  };

  useEffect(() => {
    const fetchAnnouncements = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/announcements`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        const latestAnnouncements = response.data.slice(0, 13);
        setAnnouncements(latestAnnouncements);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching announcements:', error);
        setError('Failed to fetch announcements. Please try again later.');
        setLoading(false);
      }
    };

    fetchAnnouncements();
  }, [API_URL]);

  if (loading) {
    return (
      <div className="loading-container">
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return <Alert message={error} type="error" />;
  }

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sidebar />
      <Layout>
        <Header />
        <Content className="newsletter-content-wrapper">
          <div className="newsletter-container">
            <Card className="newsletter-card">
              <div className="newsletter-inner">
                {/* Header Section */}
                <div className="newsletter-header">
                  <Title level={1} className="newsletter-title">
                    SCHOOL NEWSLETTER
                  </Title>
                </div>

                {/* Announcements Grid */}
                <div className="announcements-grid">
                  <Row gutter={[24, 24]}>
                    {announcements.map((announcement) => (
                      <Col 
                        xs={24} 
                        sm={24} 
                        md={12} 
                        lg={8} 
                        xl={8} 
                        key={announcement.id}
                      >
                        <Card
                          hoverable
                          className="announcement-card"
                        >
                          {/* Only render image container if image exists */}
                          {announcement.image_url && (
                            <div className="announcement-image-container">
                              <img
                                alt={announcement.title}
                                src={announcement.image_url}
                                className="announcement-image"
                                onError={(e) => {
                                  // Remove the image container entirely on error
                                  e.target.parentElement.style.display = 'none';
                                }}
                              />
                            </div>
                          )}
                          
                          <div className="announcement-content">
                            <Title level={4} className="announcement-title">
                              {announcement.title}
                            </Title>
                            <Paragraph className="announcement-message">
                              {announcement.message}
                            </Paragraph>
                            <div className="announcement-footer">
                              <Space className="announcement-date">
                                <CalendarOutlined />
                                <Text type="secondary">{formatDate(announcement.date)}</Text>
                              </Space>
                              {announcement.link && (
                                <a 
                                  href={announcement.link} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="announcement-link"
                                >
                                  More Info →
                                </a>
                              )}
                            </div>
                          </div>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                </div>

                <Divider className="newsletter-divider" />
              </div>
            </Card>
          </div>
        </Content>
      </Layout>
    </Layout>
  );
};

export default withRoleAccess(AnnouncementPage, ['admin', 'superadmin', 'teacher', 'student']);